import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'

export type QueryType = 'email' | 'phone' | 'username' | 'domain' | 'fullName' | 'govtId' | 'company' | 'general'

interface SourceInfo {
  name: string
  color: string
}

interface QueryTypeResult {
  type: QueryType
  label: string
  color: string
  bgColor: string
  borderColor: string
  sources: SourceInfo[]
}

const SOURCE_MAP: Record<QueryType, SourceInfo[]> = {
  email: [
    { name: 'LeakOSINT', color: 'text-red-400' },
    { name: 'Gmail OSINT', color: 'text-blue-400' },
    { name: 'RocketReach', color: 'text-purple-400' },
    { name: 'Hudson Rock', color: 'text-orange-400' },
    { name: 'OCCRP Aleph', color: 'text-amber-400' },
    { name: 'Google', color: 'text-green-400' },
  ],
  phone: [
    { name: 'LeakOSINT', color: 'text-red-400' },
    { name: 'Eyecon Caller ID', color: 'text-teal-400' },
    { name: 'Facebook Reverse', color: 'text-blue-400' },
    { name: 'OCCRP Aleph', color: 'text-amber-400' },
  ],
  username: [
    { name: 'LeakOSINT', color: 'text-red-400' },
    { name: 'Hudson Rock', color: 'text-orange-400' },
    { name: 'OCCRP Aleph', color: 'text-amber-400' },
    { name: 'Google', color: 'text-green-400' },
  ],
  domain: [
    { name: 'LeakIX', color: 'text-cyan-400' },
    { name: 'Hudson Rock', color: 'text-orange-400' },
    { name: 'OCCRP Aleph', color: 'text-amber-400' },
    { name: 'Google', color: 'text-green-400' },
  ],
  fullName: [
    { name: 'LeakOSINT', color: 'text-red-400' },
    { name: 'RocketReach', color: 'text-purple-400' },
    { name: 'OCCRP Aleph', color: 'text-amber-400' },
    { name: 'Google', color: 'text-green-400' },
  ],
  govtId: [
    { name: 'LeakOSINT', color: 'text-red-400' },
    { name: 'OCCRP Aleph', color: 'text-amber-400' },
  ],
  company: [
    { name: 'OCCRP Aleph', color: 'text-amber-400' },
    { name: 'LeakIX', color: 'text-cyan-400' },
    { name: 'Google', color: 'text-green-400' },
  ],
  general: [
    { name: 'LeakOSINT', color: 'text-red-400' },
    { name: 'Google', color: 'text-green-400' },
  ],
}

const TYPE_CONFIG: Record<QueryType, { label: string; color: string; bgColor: string; borderColor: string }> = {
  email: { label: 'Email', color: 'text-blue-300', bgColor: 'bg-blue-500/20', borderColor: 'border-blue-500/50' },
  phone: { label: 'Phone', color: 'text-teal-300', bgColor: 'bg-teal-500/20', borderColor: 'border-teal-500/50' },
  username: { label: 'Username', color: 'text-green-300', bgColor: 'bg-green-500/20', borderColor: 'border-green-500/50' },
  domain: { label: 'Domain', color: 'text-cyan-300', bgColor: 'bg-cyan-500/20', borderColor: 'border-cyan-500/50' },
  fullName: { label: 'Full Name', color: 'text-purple-300', bgColor: 'bg-purple-500/20', borderColor: 'border-purple-500/50' },
  govtId: { label: 'Govt ID', color: 'text-amber-300', bgColor: 'bg-amber-500/20', borderColor: 'border-amber-500/50' },
  company: { label: 'Company', color: 'text-orange-300', bgColor: 'bg-orange-500/20', borderColor: 'border-orange-500/50' },
  general: { label: 'General', color: 'text-gray-300', bgColor: 'bg-gray-500/20', borderColor: 'border-gray-500/50' },
}

export function detectQueryType(query: string): QueryTypeResult {
  const q = (query || '').trim()
  if (!q) return { type: 'general', ...TYPE_CONFIG.general, sources: SOURCE_MAP.general }

  // Email: contains @ and . after @
  if (q.includes('@') && q.split('@')[1]?.includes('.')) {
    return { type: 'email', ...TYPE_CONFIG.email, sources: SOURCE_MAP.email }
  }

  // Phone: 10+ digits after stripping formatting
  const digits = q.replace(/[\s\-\(\)\+\.]/g, '').replace(/\D/g, '')
  if (digits.length >= 10) {
    return { type: 'phone', ...TYPE_CONFIG.phone, sources: SOURCE_MAP.phone }
  }

  // SSN pattern (XXX-XX-XXXX)
  if (/^\d{3}-\d{2}-\d{4}$/.test(q)) {
    return { type: 'govtId', ...TYPE_CONFIG.govtId, sources: SOURCE_MAP.govtId }
  }

  // Govt ID: alphanumeric 6-12 chars with mixed letters/digits (passport, national ID)
  if (/^[A-Z0-9]{6,12}$/i.test(q) && /[A-Z]/i.test(q) && /\d/.test(q)) {
    return { type: 'govtId', ...TYPE_CONFIG.govtId, sources: SOURCE_MAP.govtId }
  }

  // Company: contains business suffixes
  if (/\b(inc|llc|ltd|corp|gmbh|pvt|co\.|company|enterprises?|solutions?|technologies?|group)\b/i.test(q)) {
    return { type: 'company', ...TYPE_CONFIG.company, sources: SOURCE_MAP.company }
  }

  // Domain: contains . without @ and looks like a domain
  if (q.includes('.') && !q.includes('@') && !q.includes(' ') && /\.[a-z]{2,}$/i.test(q)) {
    return { type: 'domain', ...TYPE_CONFIG.domain, sources: SOURCE_MAP.domain }
  }

  // Full Name: space-separated, 2+ words, each starts with capital or is all lowercase
  const words = q.split(/\s+/)
  if (words.length >= 2 && words.every(w => /^[a-zA-Z]+$/.test(w))) {
    return { type: 'fullName', ...TYPE_CONFIG.fullName, sources: SOURCE_MAP.fullName }
  }

  // Username: single word
  if (!q.includes(' ') && q.length >= 3) {
    return { type: 'username', ...TYPE_CONFIG.username, sources: SOURCE_MAP.username }
  }

  return { type: 'general', ...TYPE_CONFIG.general, sources: SOURCE_MAP.general }
}

interface QueryTypeIndicatorProps {
  query: string
  showSources?: boolean
}

export function QueryTypeIndicator({ query, showSources = true }: QueryTypeIndicatorProps) {
  const result = useMemo(() => detectQueryType(query), [query])

  if (!query.trim()) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Badge className={`${result.bgColor} ${result.color} border ${result.borderColor} font-mono text-xs`}>
          {result.label}
        </Badge>
        <span className="text-gray-400 text-xs font-mono">detected</span>
      </div>
      {showSources && (
        <div className="flex flex-wrap gap-1.5">
          {result.sources.map((source) => (
            <span
              key={source.name}
              className={`text-xs font-mono ${source.color} bg-black/40 px-2 py-0.5 rounded border border-gray-700/50`}
            >
              {source.name}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
